import type {
  ActivationAttributionInput,
  ActivationAttributionTouch,
} from "@/types/activationAttribution";

export const ACTIVATION_ATTRIBUTION_STORAGE_KEY =
  "ariaflow:activation-attribution:v1";

const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_TOUCH_AGE_MS = 180 * 24 * 60 * 60 * 1000;
const SAFE_TOKEN = /^[a-z0-9][a-z0-9._-]{0,79}$/i;
const SAFE_LOCALE = /^[a-z]{2}(?:-[a-z]{2})?$/i;
const SAFE_PATH = /^\/(?!\/)[^\s?#]{0,239}$/;
const SAFE_LABEL = /^[^<>"'@?&#=%\\\u0000-\u001f\u007f]{1,120}$/;
const SAFE_DOMAIN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const SEARCH_ENGINES: Record<string, string> = {
  "baidu.com": "baidu",
  "bing.com": "bing",
  "duckduckgo.com": "duckduckgo",
  "google.com": "google",
  "search.yahoo.com": "yahoo",
  "yandex.com": "yandex",
};

const PAID_CLICK_MARKERS: Array<{
  parameter: string;
  source: string;
  medium: string;
}> = [
  { parameter: "gclid", source: "google", medium: "paid_search" },
  { parameter: "dclid", source: "google", medium: "display" },
  { parameter: "msclkid", source: "bing", medium: "paid_search" },
  { parameter: "fbclid", source: "meta", medium: "paid_social" },
  { parameter: "ttclid", source: "tiktok", medium: "paid_social" },
  { parameter: "li_fat_id", source: "linkedin", medium: "paid_social" },
];

type StoredAttribution = {
  version: 1;
  expiresAt: string;
  attribution: ActivationAttributionInput;
};

type TouchLocationInput = {
  url: string;
  referrer?: string;
  locale?: string;
  now?: Date;
  fallbackTouch?: ActivationAttributionTouch | null;
};

type ActivationAttributionCandidate = Omit<
  Partial<ActivationAttributionInput>,
  "firstTouch" | "lastTouch"
> & {
  firstTouch?: Partial<ActivationAttributionTouch>;
  lastTouch?: Partial<ActivationAttributionTouch>;
};

const cleanToken = (value: unknown, fallback: string) => {
  const normalized = String(value || "").trim().toLowerCase();
  return SAFE_TOKEN.test(normalized) ? normalized : fallback;
};

const cleanLabel = (value: unknown) => {
  const normalized = String(value || "").trim();
  return normalized && SAFE_LABEL.test(normalized) ? normalized : null;
};

const cleanPath = (value: unknown) => {
  const normalized = String(value || "").trim();
  return SAFE_PATH.test(normalized) ? normalized : "/";
};

const cleanLocale = (value: unknown) => {
  const normalized = String(value || "en").trim().toLowerCase();
  return SAFE_LOCALE.test(normalized) ? normalized : "en";
};

const cleanDomain = (value: unknown) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");
  return SAFE_DOMAIN.test(normalized) ? normalized : null;
};

const cleanCapturedAt = (value: unknown, now: Date) => {
  const timestamp = Date.parse(String(value || ""));
  if (
    !Number.isFinite(timestamp) ||
    timestamp > now.getTime() + 5 * 60 * 1000 ||
    timestamp < now.getTime() - MAX_TOUCH_AGE_MS
  ) {
    return now.toISOString();
  }
  return new Date(timestamp).toISOString();
};

const resolveSearchEngine = (hostname: string) => {
  const domain = cleanDomain(hostname);
  if (!domain) return null;

  for (const [candidate, source] of Object.entries(SEARCH_ENGINES)) {
    if (domain === candidate || domain.endsWith(`.${candidate}`)) return source;
  }
  return null;
};

const createVisitorId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "00000000-0000-4000-8000-000000000000";
};

export function normalizeActivationTouch(
  value: Partial<ActivationAttributionTouch> | null | undefined,
  now = new Date(),
): ActivationAttributionTouch | null {
  if (!value) return null;

  return {
    source: cleanToken(value.source, "direct"),
    medium: cleanToken(value.medium, "direct"),
    campaign: cleanLabel(value.campaign),
    content: cleanLabel(value.content),
    term: cleanLabel(value.term),
    landingPath: cleanPath(value.landingPath),
    ctaLocation: cleanToken(value.ctaLocation, "unknown"),
    locale: cleanLocale(value.locale),
    referrerDomain: cleanDomain(value.referrerDomain),
    capturedAt: cleanCapturedAt(value.capturedAt, now),
  };
}

export function normalizeActivationAttribution(
  value: ActivationAttributionCandidate | null | undefined,
  now = new Date(),
): ActivationAttributionInput | null {
  if (!value || !UUID_PATTERN.test(String(value.visitorId || ""))) return null;

  const firstTouch = normalizeActivationTouch(value.firstTouch, now);
  const lastTouch = normalizeActivationTouch(value.lastTouch, now);
  if (!firstTouch || !lastTouch) return null;

  return {
    visitorId: String(value.visitorId).toLowerCase(),
    firstTouch,
    lastTouch:
      Date.parse(lastTouch.capturedAt) >= Date.parse(firstTouch.capturedAt)
        ? lastTouch
        : { ...lastTouch, capturedAt: firstTouch.capturedAt },
  };
}

export function attributionTouchFromLocation({
  url,
  referrer = "",
  locale = "en",
  now = new Date(),
  fallbackTouch = null,
}: TouchLocationInput): ActivationAttributionTouch {
  const currentUrl = new URL(url);
  const searchParams = currentUrl.searchParams;
  const campaignSource = searchParams.get("utm_source");
  const campaignMedium = searchParams.get("utm_medium");
  const paidClick = PAID_CLICK_MARKERS.find(({ parameter }) =>
    searchParams.has(parameter),
  );
  const ctaIntent = searchParams.get("intent") === "faceless-video";
  const ctaSource = searchParams.get("source");
  const ctaLandingPath = searchParams.get("landingPath");
  const ctaLocation = searchParams.get("location");
  let referrerDomain: string | null = null;

  try {
    const referrerUrl = referrer ? new URL(referrer) : null;
    if (referrerUrl && referrerUrl.origin !== currentUrl.origin) {
      referrerDomain = cleanDomain(referrerUrl.hostname);
    }
  } catch {
    referrerDomain = null;
  }

  if (ctaIntent && fallbackTouch && !campaignSource && !referrerDomain) {
    return {
      ...fallbackTouch,
      landingPath: cleanPath(ctaLandingPath || fallbackTouch.landingPath),
      ctaLocation: cleanToken(ctaLocation, fallbackTouch.ctaLocation),
      locale: cleanLocale(searchParams.get("locale") || locale),
    };
  }

  const organicSource = referrerDomain
    ? resolveSearchEngine(referrerDomain)
    : null;
  const source = campaignSource
    ? cleanToken(campaignSource, "unknown")
    : paidClick?.source ||
      organicSource ||
      referrerDomain ||
      (ctaIntent ? "ariaflow" : "direct");
  const medium = campaignSource
    ? cleanToken(campaignMedium, "campaign")
    : paidClick?.medium ||
      (organicSource
      ? "organic"
      : referrerDomain
        ? "referral"
        : ctaIntent
          ? "owned"
          : "direct");

  return {
    source,
    medium,
    campaign: cleanLabel(
      searchParams.get("utm_campaign") || (ctaIntent ? ctaSource : null),
    ),
    content: cleanLabel(searchParams.get("utm_content")),
    term: cleanLabel(searchParams.get("utm_term")),
    landingPath: cleanPath(ctaLandingPath || currentUrl.pathname),
    ctaLocation: cleanToken(ctaLocation, "unknown"),
    locale: cleanLocale(searchParams.get("locale") || locale),
    referrerDomain,
    capturedAt: now.toISOString(),
  };
}

export function readActivationAttribution(
  storage: Pick<Storage, "getItem" | "removeItem">,
  now = new Date(),
): ActivationAttributionInput | null {
  try {
    const raw = storage.getItem(ACTIVATION_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredAttribution>;
    if (
      stored.version !== 1 ||
      Date.parse(String(stored.expiresAt || "")) <= now.getTime()
    ) {
      storage.removeItem(ACTIVATION_ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return normalizeActivationAttribution(stored.attribution, now);
  } catch {
    return null;
  }
}

export function persistActivationAttribution(
  storage: Pick<Storage, "setItem">,
  attribution: ActivationAttributionInput,
  now = new Date(),
) {
  const normalized = normalizeActivationAttribution(attribution, now);
  if (!normalized) return null;

  const stored: StoredAttribution = {
    version: 1,
    expiresAt: new Date(now.getTime() + ATTRIBUTION_TTL_MS).toISOString(),
    attribution: normalized,
  };
  storage.setItem(ACTIVATION_ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
  return normalized;
}

export function captureActivationAttribution({
  storage,
  url,
  referrer = "",
  locale = "en",
  now = new Date(),
}: TouchLocationInput & { storage: Storage }): ActivationAttributionInput {
  const existing = readActivationAttribution(storage, now);
  const touch = attributionTouchFromLocation({
    url,
    referrer,
    locale,
    now,
    fallbackTouch: existing?.lastTouch,
  });
  const isDirectReturn =
    touch.source === "direct" &&
    touch.medium === "direct" &&
    existing?.lastTouch.source !== "direct";
  const attribution: ActivationAttributionInput = {
    visitorId: existing?.visitorId || createVisitorId(),
    firstTouch: existing?.firstTouch || touch,
    lastTouch: isDirectReturn && existing ? existing.lastTouch : touch,
  };

  return persistActivationAttribution(storage, attribution, now) || attribution;
}

export function updateActivationCtaContext({
  storage,
  source,
  landingPath,
  ctaLocation,
  locale,
  now = new Date(),
}: {
  storage: Storage;
  source: string;
  landingPath: string;
  ctaLocation: string;
  locale: string;
  now?: Date;
}) {
  const existing = readActivationAttribution(storage, now);
  const touch: ActivationAttributionTouch = existing?.lastTouch
    ? {
        ...existing.lastTouch,
        landingPath: cleanPath(landingPath),
        ctaLocation: cleanToken(ctaLocation, "unknown"),
        locale: cleanLocale(locale),
      }
    : {
        source: "ariaflow",
        medium: "owned",
        campaign: cleanLabel(source),
        content: null,
        term: null,
        landingPath: cleanPath(landingPath),
        ctaLocation: cleanToken(ctaLocation, "unknown"),
        locale: cleanLocale(locale),
        referrerDomain: null,
        capturedAt: now.toISOString(),
      };

  return persistActivationAttribution(
    storage,
    {
      visitorId: existing?.visitorId || createVisitorId(),
      firstTouch: existing?.firstTouch || touch,
      lastTouch: touch,
    },
    now,
  );
}
